/**
 * «Что видно за период» — пояснение под графиком «Аналитики» (Pro).
 *
 * Числа — из `chartFacts` ядра. Слова — от модели через сервер, а когда её
 * нет (нет сети, модель ошиблась) — по шаблону из тех же фактов. Поэтому
 * числа в карточке всегда те же, что на графике.
 *
 * Пояснение переписывается, как только изменились его факты: добавили,
 * удалили или поправили операцию так, что поменялись суммы, — при следующем
 * заходе (или сразу, если экран открыт) модель спрашивается заново. Ничего не
 * менялось — показывается сохранённое, без нового вызова.
 */
import { useEffect, useState } from 'react';
import { formatMoney, type ChartFacts, type PlainDate } from '@skoka/core';
import { api, unwrap } from './sync.js';
import { dayMonth, plural } from './text.js';

const STORE = 'skoka.chartNotes';
/** `at` — когда получен ответ: его время видно в карточке. */
type Saved = { fp: string; at: string; text: string | null };

function load(): Record<string, Saved> {
  try { return JSON.parse(localStorage.getItem(STORE) ?? '{}') as Record<string, Saved>; } catch { return {}; }
}
function save(view: string, value: Saved): void {
  try {
    const all = load();
    const keep = Object.fromEntries(Object.entries(all).filter(([k]) => k !== view).slice(-19));
    localStorage.setItem(STORE, JSON.stringify({ ...keep, [view]: value }));
  } catch { /* без памяти — спросим снова */ }
}

const MONTHS_NOM = ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'];
/** Ключ столбца словами: «7 октября» или «октябрь». */
export const bucketTitle = (key: string, today: PlainDate): string => (key.length === 7
  ? `${MONTHS_NOM[Number(key.slice(5, 7)) - 1] ?? key}${key.slice(0, 4) !== today.slice(0, 4) ? ` ${key.slice(0, 4)}` : ''}`
  : dayMonth(key, today));

/** Текст по шаблону — те же факты, что получает модель. */
export function notesTemplate(f: ChartFacts, today: PlainDate): string[] {
  const out: string[] = [];
  const top = f.top[0];
  const per = f.unit === 'month' ? 'в месяц' : 'в день';
  if (f.flow === 'expense') {
    if (top) out.push(`Больше всего ушло на «${top.name}» — ${formatMoney(top.sum)}${top.count ? ` за ${plural(top.count, 'покупку', 'покупки', 'покупок')}` : ''}.`);
    if (f.peak) {
      const day = f.unit === 'month' ? `Самый дорогой месяц — ${bucketTitle(f.peak.key, today)}` : `Самый дорогой день — ${bucketTitle(f.peak.key, today)}`;
      out.push(`${day}: «${f.peak.name}» на ${formatMoney(f.peak.nameSum)}. Без него выходит около ${formatMoney(f.peak.restAverage)} ${per}.`);
    } else {
      out.push(`В среднем уходит ${formatMoney(f.average)} ${per}.`);
    }
    if (f.want) {
      out.push(`На свободные траты ушло ${formatMoney(f.want.spent)} из обычных ${formatMoney(f.want.usual)} в месяц`
        + ` — прошло ${f.want.daysPassed} из ${f.want.daysTotal} дней.`);
    }
  } else {
    if (top) out.push(`Больше всего пришло: «${top.name}» — ${formatMoney(top.sum)}.`);
    out.push(`Всего ${plural(f.count, 'поступление', 'поступления', 'поступлений')} на ${formatMoney(f.total)}.`);
    if (f.peak) out.push(`Больше всего пришло ${bucketTitle(f.peak.key, today)} — ${formatMoney(f.peak.sum)}.`);
  }
  return out;
}

/** Фразы — каждая своим абзацем: так их читают, а не продираются. */
export const sentences = (text: string): string[] => text.split(/(?<=[.!?])\s+(?=[А-ЯЁA-Z«])/u).filter(Boolean);

export interface ChartNotes {
  /** Слова модели или `null` — тогда шаблон. */
  text: string | null;
  /** Когда пояснение обновилось — ISO; `null`, пока не обновлялось. */
  at: string | null;
  /** Факты изменились, ответ ещё в пути. */
  loading: boolean;
}

/**
 * Пояснение для этого вида графика (период, счёт, расходы или поступления).
 * Спрашивает модель только при `enabled` (Pro и пробный период) и только
 * когда факты не те, на которые уже был ответ.
 */
export function useChartNotes(facts: ChartFacts | null, view: string, enabled: boolean): ChartNotes {
  const fp = facts ? JSON.stringify(facts) : '';
  const known = (): ChartNotes | null => {
    const s = load()[view];
    return s && s.fp === fp ? { text: s.text, at: s.at, loading: false } : null;
  };
  const [notes, setNotes] = useState<ChartNotes>(() => known() ?? { text: null, at: null, loading: false });
  useEffect(() => {
    if (!facts || !enabled) { setNotes({ text: null, at: null, loading: false }); return; }
    const saved = known();
    if (saved) { setNotes(saved); return; }
    // Факты другие — до ответа шаблон с новыми числами, а не прежние слова.
    setNotes({ text: null, at: null, loading: true });
    let live = true;
    void (async () => {
      try {
        const body = await unwrap(await api('/ai/chart-notes', { method: 'POST', body: JSON.stringify({ facts }) }));
        const got = typeof (body as { text?: unknown }).text === 'string' ? (body as { text: string }).text : null;
        const at = new Date().toISOString();
        save(view, { fp, at, text: got });
        if (live) setNotes({ text: got, at, loading: false });
      } catch {
        // Сеть или предел — шаблон без запоминания: в другой раз спросим снова.
        if (live) setNotes({ text: null, at: new Date().toISOString(), loading: false });
      }
    })();
    return () => { live = false; };
  }, [fp, view, enabled]); // eslint-disable-line react-hooks/exhaustive-deps
  return notes;
}

/** «сегодня, 10:42», «вчера, 21:05», «12 октября, 09:30» — по часам устройства. */
export function updatedText(at: string, today: PlainDate): string {
  const d = new Date(at);
  const pad = (n: number) => String(n).padStart(2, '0');
  const ymd = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const y = new Date(`${today}T12:00:00Z`); y.setUTCDate(y.getUTCDate() - 1);
  const day = ymd === today ? 'сегодня' : ymd === y.toISOString().slice(0, 10) ? 'вчера' : dayMonth(ymd, today);
  return `${day}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
